import { useCallback, useMemo } from "react"
import * as R from "remeda"

import type { CustomerNeed, Issue } from "@productlane/zero"
import { sortByCustomOrder } from "@productlane/lib/src/roadmap-custom-order"

import { isHiddenFromRequestList } from "./request-list-visibility"
import { useSortState } from "./states/global-state"
import { useFilteredIssues } from "./use-team-filter"
import { useZeroWorkspace } from "./zero"

export type RoadmapAugmentedIssue = Issue & {
	customerNeeds: readonly CustomerNeed[]
}

export function getAugmentedIssue({ issue }: { issue: RoadmapAugmentedIssue }) {
	return {
		...issue,
		importance: {
			score: issue.customerNeeds.length,
		},
	}
}

export function getSortedIssues({
	issues,
	sortOrder,
	groupByImportance,
}: {
	issues: readonly RoadmapAugmentedIssue[]
	sortOrder: "LINEAR" | "IMPORTANCE"
	groupByImportance: boolean
}) {
	const augmented = []
	for (const issue of issues) {
		const augmentedIssue = getAugmentedIssue({
			issue,
		})
		augmented.push(augmentedIssue)
	}

	if (sortOrder === "LINEAR") {
		return R.sortBy(augmented.flat(), [(i) => i.sortOrder, "asc"])
	}

	return R.sortBy(augmented, [
		(x) => x.importance.score ?? 0,
		groupByImportance ? "desc" : "asc",
	]) as Array<Issue>
}

export function useSortedIssues({
	issues,
}: {
	issues: readonly RoadmapAugmentedIssue[]
}) {
	const { groupByImportance } = useSortState()
	const workspace = useZeroWorkspace()
	const filteredIssues = useFilteredIssues(issues)

	const sortOrder = workspace?.roadmapSettings?.sortOrder
	const scoreDirection =
		sortOrder === "SCORE_ASC" ? "asc" : groupByImportance ? "desc" : "asc"

	const sortIssues = useCallback(
		(issues: Array<Issue>) => {
			return R.sortBy(issues, [
				(issue) => issue.totalScore ?? 0,
				scoreDirection,
			])
		},
		[scoreDirection],
	)

	const sortedIssues = useMemo(() => {
		const augmented = []
		for (const issue of filteredIssues) {
			const augmentedIssue = getAugmentedIssue({
				issue,
			})
			augmented.push(augmentedIssue)
		}

		if (sortOrder === "LINEAR" || sortOrder === "LINEAR_REVERSE") {
			return R.sortBy(augmented.flat(), [
				(i) => i.sortOrder,
				sortOrder === "LINEAR" ? "asc" : "desc",
			])
		}
		if (sortOrder === "CUSTOM") {
			return sortByCustomOrder(
				augmented,
				(i) => i.portalSortOrder,
				(i) => i.totalScore,
			)
		}

		const sorted: Array<Issue> = R.sortBy(augmented, [
			(x) => x.importance.score ?? 0,
			scoreDirection,
		])

		return sortIssues(sorted)
	}, [filteredIssues, scoreDirection, sortIssues, sortOrder])

	return {
		sortedIssues,
	}
}

const issueStatusGroupOrder: Record<string, number> = {
	triage: 0,
	started: 1,
	unstarted: 2,
	backlog: 3,
}

export function useCategorizedIssues(
	issues: readonly Issue[],
): Record<string, Issue[]> {
	return useMemo(() => {
		if (!issues) return {}

		const filteredIssues = issues.filter(
			(issue) => !isHiddenFromRequestList(issue),
		)

		const sortByPriority = (items: Issue[]) =>
			R.sortBy(items, [(i) => i.priority ?? 4, "asc"])

		const grouped = R.groupBy(filteredIssues, (issue) =>
			issue.statusName
				? `${issue.statusName}`
				: `${issue.status}---${issue.statusColor}`,
		)

		const sortedEntries = Object.entries(grouped).sort(
			([, issuesA], [, issuesB]) => {
				const statusA = issuesA[0]?.status ?? "backlog"
				const statusB = issuesB[0]?.status ?? "backlog"
				const orderA = issueStatusGroupOrder[statusA] ?? 3
				const orderB = issueStatusGroupOrder[statusB] ?? 3
				return orderA - orderB
			},
		)

		const orderedResult: Record<string, Issue[]> = {}
		for (const [key, items] of sortedEntries) {
			orderedResult[key] = sortByPriority(items)
		}

		return orderedResult
	}, [issues])
}
